import { useContext, useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, LogOut, Settings, Menu, X, ChevronRight, Bell, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import LanguageSwitcher from './LanguageSwitcher';
import CurrencySelector from './CurrencySelector';
import { LanguageContext, axiosInstance } from '../App';
import { isKayiComNativeApp } from '../utils/androidApp';
import InstallAppButton from './InstallAppButton';

const Navbar = ({ user, logout, cartItemCount, settings }) => {
  const { t } = useContext(LanguageContext);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [sideMenuOpen, setSideMenuOpen] = useState(false);
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [inNativeApp, setInNativeApp] = useState(false);

  useEffect(() => {
    setInNativeApp(isKayiComNativeApp());
  }, []);

  const fetchBadges = useCallback(async () => {
    if (!user) return;
    const userId = user.user_id || user.id;
    try {
      const [notifRes, msgRes] = await Promise.all([
        axiosInstance.get(`/notifications/unread-count?user_id=${userId}`),
        axiosInstance.get(`/messages/unread-count?user_id=${userId}`),
      ]);
      setUnreadNotifs(notifRes.data?.unread_count || 0);
      setUnreadMessages(msgRes.data?.unread || 0);
    } catch {}
  }, [user]);

  useEffect(() => {
    fetchBadges();
    const intervalMs = inNativeApp ? 90000 : 30000;
    const interval = setInterval(fetchBadges, intervalMs);
    const onVis = () => {
      if (document.visibilityState === 'visible') fetchBadges();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [fetchBadges, inNativeApp]);

  useEffect(() => {
    if (!sideMenuOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [sideMenuOpen]);

  const closeSideMenu = () => setSideMenuOpen(false);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      closeSideMenu();
    }
  };

  const mainLinks = [
    { name: 'Home', icon: '🏠', href: '/' },
    { name: 'Marketplace', icon: '🏪', href: '/marketplace', accent: true, soon: true },
    { name: 'Products', icon: '📦', href: '/products' },
    { name: 'Blog', icon: '📰', href: '/blog' },
    { name: 'Help', icon: '❓', href: '/help' },
  ].filter(Boolean);

  const categoryLinks = [
    { name: 'Gift Cards', icon: '🎁', href: '/products/giftcard' },
    { name: 'Subscriptions', icon: '🔄', href: '/products/subscription' },
    { name: 'Game Top-Up', icon: '🎮', href: '/products/topup' },
    { name: 'Services', icon: '⚡', href: '/products/service' },
  ];

  const accountLinks = user
    ? [
        { name: t('myAccount'), icon: '👤', href: '/dashboard' },
        { name: 'Wallet', icon: '💳', href: '/wallet' },
        { name: 'Topup Mobile', icon: '📲', href: '/mobile-topup' },
        { name: 'Referral', icon: '👥', href: '/referral' },
        { name: 'Withdraw', icon: '💰', href: '/withdraw' },
      ]
    : [{ name: t('login'), icon: '🔐', href: '/login' }];

  const menuLinkClass = (link, options = {}) =>
    `flex items-center justify-between px-3 py-3 rounded-xl text-white/85 hover:bg-white/5 transition ${
      options.accent || link.accent ? 'text-green-400' : ''
    }`;

  const renderMenuLink = (link, options = {}) => {
    const inner = (
      <>
        <span className="flex items-center gap-3 min-w-0">
          <span className="text-base flex-shrink-0">{link.icon}</span>
          <span className="text-sm font-medium truncate">{link.name}</span>
          {link.soon && (
            <span className="text-[10px] uppercase tracking-wide text-yellow-300/90">Coming soon</span>
          )}
          {options.badge > 0 && (
            <span className="ml-1 min-w-[1.25rem] h-5 px-1 rounded-full bg-green-500 text-black text-[10px] font-bold flex items-center justify-center">
              {options.badge > 99 ? '99+' : options.badge}
            </span>
          )}
        </span>
        <ChevronRight size={16} className="text-white/25 flex-shrink-0" />
      </>
    );

    if (link.download) {
      return (
        <a
          key={`${link.href}-${link.name}`}
          href={link.href}
          download={link.download}
          className={menuLinkClass(link, options)}
          onClick={closeSideMenu}
          data-testid="menu-download-app"
        >
          {inner}
        </a>
      );
    }

    return (
      <Link
        key={`${link.href}-${link.name}`}
        to={link.href}
        className={menuLinkClass(link, options)}
        onClick={closeSideMenu}
      >
        {inner}
      </Link>
    );
  };

  return (
    <>
      <nav className="sticky top-0 z-50 w-full bg-[#111] border-b border-white/5">
        {settings?.announcement_enabled && settings?.announcement_message && (
          <div className="w-full bg-green-500 text-black">
            <div className="w-full max-w-[1400px] mx-auto px-3 py-1.5 text-sm font-medium">
              <div className="announcement-marquee">
                <div className="announcement-marquee__content">
                  {settings.announcement_message}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="w-full max-w-[1400px] mx-auto px-3 md:px-6 py-2 md:py-3">
          <div className="flex items-center justify-between gap-2 md:gap-4">
            <Link to="/" className="flex items-center flex-shrink-0 min-w-0" data-testid="nav-logo">
              {settings?.logo_url ? (
                <img src={settings.logo_url} alt="Logo" className="h-8 md:h-12 w-auto max-w-[140px] md:max-w-none" />
              ) : (
                <img src="/images/kayeecomlogo.png" alt="KayiCom" className="h-8 md:h-12 w-auto max-w-[140px] md:max-w-none" />
              )}
            </Link>

            <div className="hidden lg:flex items-center space-x-5 text-sm">
              <Link to="/" className="text-white/80 font-medium hover:text-green-400 transition" data-testid="nav-home">
                Home
              </Link>
              <Link to="/marketplace" className="text-white/80 font-medium hover:text-green-400 transition" data-testid="nav-marketplace">
                Marketplace
                <span className="ml-1.5 text-[10px] uppercase tracking-wide text-yellow-300/90">Coming soon</span>
              </Link>
              <Link to="/products" className="text-white/80 font-medium hover:text-green-400 transition">
                Products
              </Link>
              <Link to="/blog" className="text-white/80 font-medium hover:text-green-400 transition" data-testid="nav-blog">
                Blog
              </Link>
              <InstallAppButton variant="navlink" />
              {user && (
                <>
                  <Link to="/dashboard" className="text-white/80 font-medium hover:text-green-400 transition">
                    Dashboard
                  </Link>
                  <Link to="/mobile-topup" className="text-white/80 font-medium hover:text-green-400 transition">
                    Topup Mobile
                  </Link>
                  <Link to="/withdraw" className="text-white/80 font-medium hover:text-green-400 transition">
                    Withdraw
                  </Link>
                </>
              )}
            </div>

            <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
              <CurrencySelector />
              <LanguageSwitcher />

              <InstallAppButton variant="icon" />

              <Link to="/cart" className="relative" data-testid="nav-cart">
                <Button variant="ghost" size="sm" className="text-white/70 hover:bg-white/5 hover:text-green-400 p-1.5 md:p-2">
                  <ShoppingCart size={18} className="md:w-5 md:h-5" />
                  {cartItemCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-green-500 text-black text-[10px] rounded-full h-4 w-4 md:h-5 md:w-5 flex items-center justify-center font-bold">
                      {cartItemCount}
                    </span>
                  )}
                </Button>
              </Link>

              {user && (
                <>
                  <Link to="/messages" className="relative hidden lg:block" data-testid="nav-messages">
                    <Button variant="ghost" size="sm" className="text-white/70 hover:bg-white/5 hover:text-green-400 p-1.5 md:p-2">
                      <MessageCircle size={18} className="md:w-5 md:h-5" />
                      {unreadMessages > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 bg-blue-500 text-white text-[10px] rounded-full h-4 w-4 md:h-5 md:w-5 flex items-center justify-center font-bold">
                          {unreadMessages > 99 ? '99+' : unreadMessages}
                        </span>
                      )}
                    </Button>
                  </Link>
                  <Link to="/notifications" className="relative hidden lg:block" data-testid="nav-notifications">
                    <Button variant="ghost" size="sm" className="text-white/70 hover:bg-white/5 hover:text-green-400 p-1.5 md:p-2">
                      <Bell size={18} className="md:w-5 md:h-5" />
                      {unreadNotifs > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] rounded-full h-4 w-4 md:h-5 md:w-5 flex items-center justify-center font-bold">
                          {unreadNotifs > 99 ? '99+' : unreadNotifs}
                        </span>
                      )}
                    </Button>
                  </Link>
                </>
              )}

              {user ? (
                <div className="hidden lg:block">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-white/70 hover:bg-white/5 hover:text-green-400 p-1.5 md:p-2" data-testid="user-menu">
                        <User size={18} className="md:w-5 md:h-5" />
                        <span className="ml-1.5 hidden xl:inline text-sm">{user.full_name}</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48 bg-[#1a1a1a] border-white/10">
                      {user.role === 'admin' && (
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="cursor-pointer text-gray-300 hover:text-green-400" data-testid="admin-link">
                            <Settings className="mr-2" size={16} />
                            {t('adminPanel')}
                          </Link>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem asChild>
                        <Link to="/wallet" className="cursor-pointer text-gray-300 hover:text-green-400">
                          💳 Wallet
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/mobile-topup" className="cursor-pointer text-gray-300 hover:text-green-400">
                          📲 Topup Mobile
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={logout} className="cursor-pointer text-gray-300 hover:text-green-400" data-testid="logout-btn">
                        <LogOut className="mr-2" size={16} />
                        {t('logout')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ) : (
                <Link to="/login" className="hidden lg:block">
                  <Button variant="ghost" size="sm" className="text-white/70 hover:bg-white/5 hover:text-green-400 p-1.5 md:p-2" data-testid="login-btn">
                    <User size={18} className="md:w-5 md:h-5" />
                    <span className="ml-1 text-xs md:text-sm">{t('login')}</span>
                  </Button>
                </Link>
              )}

              <button
                type="button"
                className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-xl border border-white/10 bg-white/5 text-white/80 hover:text-green-400 hover:border-green-500/40 hover:bg-green-500/10 transition"
                onClick={() => setSideMenuOpen(true)}
                aria-label="Open menu"
                aria-expanded={sideMenuOpen}
              >
                <Menu size={20} />
              </button>
            </div>
          </div>

          <form onSubmit={handleSearch} className="mt-2 md:mt-3 lg:hidden">
            <div className="flex w-full">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search your products..."
                className="flex-1 min-w-0 bg-white/5 border border-white/10 rounded-l-lg px-3 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-green-500/50"
              />
              <button
                type="submit"
                className="bg-green-500 hover:bg-green-600 text-black font-semibold px-4 py-2.5 rounded-r-lg text-sm transition flex-shrink-0"
              >
                Search
              </button>
            </div>
          </form>

          <form onSubmit={handleSearch} className="mt-2 md:mt-3 hidden lg:block">
            <div className="flex">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search your products..."
                className="flex-1 bg-white/5 border border-white/10 rounded-l-lg px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-green-500/50"
              />
              <button
                type="submit"
                className="bg-green-500 hover:bg-green-600 text-black font-semibold px-5 py-2.5 rounded-r-lg text-sm transition"
              >
                Search
              </button>
            </div>
          </form>
        </div>
      </nav>

      {sideMenuOpen && (
        <div className="fixed inset-0 z-[60] flex lg:hidden">
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={closeSideMenu} aria-hidden="true" />
          <div className="relative ml-auto w-[min(20rem,100vw)] h-full overflow-y-auto bg-[#101010] border-l border-white/10 shadow-2xl shadow-black/50">
            <div className="flex items-center justify-between p-4 border-b border-white/10 bg-[#141414]">
              {settings?.logo_url ? (
                <img src={settings.logo_url} alt="Logo" className="h-8 w-auto max-w-[140px]" />
              ) : (
                <img src="/images/kayeecomlogo.png" alt="KayiCom" className="h-8 w-auto max-w-[140px]" />
              )}
              <button
                type="button"
                onClick={closeSideMenu}
                className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-white/10 bg-white/5 text-white/70 hover:text-white hover:border-red-400/40 hover:bg-red-500/10 transition"
                aria-label="Close menu"
              >
                <X size={18} />
              </button>
            </div>

            {user && (
              <div className="px-4 py-3 border-b border-white/5 bg-white/[0.02]">
                <p className="text-white text-sm font-semibold truncate">{user.full_name || user.email}</p>
                <p className="text-white/40 text-xs truncate">{user.email}</p>
              </div>
            )}

            <div className="p-3 border-b border-white/5">
              <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-white/40">Menu</p>
              {mainLinks.map((link) => renderMenuLink(link))}
              <InstallAppButton variant="menu" onClick={closeSideMenu} />
            </div>

            <div className="p-3 border-b border-white/5">
              <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-white/40">Categories</p>
              {categoryLinks.map((link) => renderMenuLink(link))}
            </div>

            <div className="p-3 border-b border-white/5">
              <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-white/40">
                {t('legalPagesNav')}
              </p>
              {[
                { name: t('termsConditions'), icon: '📄', href: '/terms' },
                { name: t('privacyPolicy'), icon: '🔒', href: '/privacy-policy' },
                { name: t('refundPolicy'), icon: '↩️', href: '/refund-policy' },
                { name: t('amlPolicy'), icon: '🛡️', href: '/aml-policy' },
                { name: t('helpCenter'), icon: '❓', href: '/help' },
              ].map((link) => renderMenuLink(link))}
            </div>

            <div className="p-3 border-b border-white/5">
              <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-white/40">Account</p>
              {accountLinks.map((link) => renderMenuLink(link))}
              {user && (
                <>
                  {renderMenuLink({ name: 'Messages', icon: '💬', href: '/messages' }, { badge: unreadMessages })}
                  {renderMenuLink({ name: 'Notifications', icon: '🔔', href: '/notifications' }, { badge: unreadNotifs })}
                  {user.role === 'admin' && renderMenuLink({ name: t('adminPanel'), icon: '⚙️', href: '/admin' })}
                  <button
                    type="button"
                    onClick={() => {
                      closeSideMenu();
                      logout();
                    }}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-red-300 hover:bg-red-500/10 transition text-sm"
                  >
                    <LogOut size={16} />
                    {t('logout')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
